/**
 * Per-viewer column visibility for the All Deployments table, persisted in
 * localStorage. Every storage access is wrapped in try/catch: the accessor can
 * throw (blocked site data, private windows) and the stored value can be missing
 * or corrupt, in which case the defaults apply and the page still renders.
 */

export const COLUMN_PREFS_KEY = 'ssp.deployments.columns.v1'

type StorageLike = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

function defaultStorage(): StorageLike | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage : null
  } catch {
    return null
  }
}

/**
 * The visible column ids, in `allIds` order. Unknown ids (e.g. columns that were
 * removed, like "costCenter" and "expiry") are dropped silently; a missing or
 * malformed stored value, or one that held nothing but unknown ids, yields
 * `defaults`. An explicitly empty selection stays empty.
 */
export function readColumnPrefs(
  allIds: readonly string[],
  defaults: readonly string[],
  storage: StorageLike | null = defaultStorage(),
  key: string = COLUMN_PREFS_KEY,
): string[] {
  const fallback = () => allIds.filter((id) => defaults.includes(id))
  try {
    const raw = storage?.getItem(key)
    if (!raw) return fallback()
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) return fallback()
    const stored = parsed.filter((v): v is string => typeof v === 'string')
    const wanted = new Set(stored)
    const visible = allIds.filter((id) => wanted.has(id))
    return stored.length > 0 && visible.length === 0 ? fallback() : visible
  } catch {
    return fallback()
  }
}

/** Returns false (and does not throw) when storage is unavailable. */
export function writeColumnPrefs(
  visible: readonly string[],
  storage: StorageLike | null = defaultStorage(),
  key: string = COLUMN_PREFS_KEY,
): boolean {
  try {
    if (!storage) return false
    storage.setItem(key, JSON.stringify(visible))
    return true
  } catch {
    return false
  }
}

export function clearColumnPrefs(
  storage: StorageLike | null = defaultStorage(),
  key: string = COLUMN_PREFS_KEY,
): void {
  try {
    storage?.removeItem(key)
  } catch {
    // Nothing to do: the defaults apply on the next read.
  }
}

/** Adds or removes one column id, keeping `allIds` order. */
export function toggleColumnId(
  visible: readonly string[],
  id: string,
  allIds: readonly string[],
): string[] {
  const next = new Set(visible)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  return allIds.filter((candidate) => next.has(candidate))
}
